import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import styles from './styles.module.css'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import LocationDropDown from '../../../../../global/components/location-drop-down/LocationDropDown'
import { ILocation } from '../../../../../../../app/entities/Location'
import { getLocationByLocationId } from '../../api'
import { fetchData } from '../../../../../global/utils/fetch'
import hasPermission from '../../../../../global/utils/user/has-permission'
import { UserContext } from '../../../../../base/components/base-container/BaseContainer'
import { PERMISSIONS } from '../../../../../global/constants/permissions'
import { formatDateYyyyMmDd } from '../../../../../global/utils/date/date-utils-yyyy-mm-dd'
import { ITechnicianPartial } from '../../../../../../../app/entities/Technician'

interface ITechnicianInputBarProps {
  technicianData: ITechnicianPartial
  setTechnicianData: Dispatch<SetStateAction<ITechnicianPartial>>
  location: ILocation | null
  setLocation: Dispatch<SetStateAction<ILocation | null>>
}

export default function TechInputBar({
  technicianData,
  setTechnicianData,
  location,
  setLocation,
}: ITechnicianInputBarProps) {
  const { user } = useContext(UserContext)

  const [shouldAllowLocationEdit, setShouldAllowLocationEdit] =
    useState<boolean>(false)
  const [showDeactivationWarning, setShowDeactivationWarning] = 
    useState<boolean>(false)

  const { employeeId, samsaraId, inactive, employmentStartDate } =
    technicianData

  function updateTechForm(e: ChangeEvent<HTMLInputElement>) {
    setTechnicianData({
      ...technicianData,
      [e.target.name]: e.target.value,
    })
  }

  function updateStartDate(e: ChangeEvent<HTMLInputElement>) {
    setTechnicianData({
      ...technicianData,
      employmentStartDate: new Date(e.target.value),
    })
  }

  async function updateLocation(locationObjectId: string) {
    const newLocation = await fetchData<ILocation>(
      getLocationByLocationId(locationObjectId),
    )
    setLocation(newLocation)
  }

  function activeStatusHandler(newStatus: boolean) {
    // If deactivating a technician in a day-settings-only location, show warning
    if (newStatus === true && location?.daySettingsOnlyMode) {
      setShowDeactivationWarning(true)
      return
    }
    
    setTechnicianData({ ...technicianData, inactive: newStatus })
  }

  function confirmDeactivation() {
    setTechnicianData({ ...technicianData, inactive: true })
    setShowDeactivationWarning(false)
  }

  function cancelDeactivation() {
    setShowDeactivationWarning(false)
  }

  useEffect(() => {
    const hasEditTechnicianLocationPermissions = hasPermission(user).allow(
      PERMISSIONS.TECHNICIANS.CATEGORY,
      PERMISSIONS.TECHNICIANS.ENTRIES.EDIT_LOCATION.NAME,
    )

    const isCreatingNewTechnician = technicianData.objectId === ''

    setShouldAllowLocationEdit(
      hasEditTechnicianLocationPermissions || isCreatingNewTechnician,
    )
  }, [technicianData.objectId, user])

  return (
    <>
      <div className={styles.mainContainer}>
        <div className={styles.flexEnd}>
          <TextField
            label='Employee Id'
            name='employeeId'
            value={employeeId || ''}
            sx={{ width: 150 }}
            onChange={updateTechForm}
          />
          <TextField
            label='Samsara Id'
            name='samsaraId'
            type='number'
            value={samsaraId || ''}
            sx={{ width: 150 }}
            onChange={updateTechForm}
          />
          <TextField
            label='Employment Start Date'
            type='date'
            value={
              employmentStartDate
                ? formatDateYyyyMmDd(new Date(employmentStartDate))
                : ''
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
            onChange={updateStartDate}
            disabled={
              !hasPermission(user).allow(
                PERMISSIONS.TECHNICIANS.CATEGORY,
                PERMISSIONS.TECHNICIANS.ENTRIES.EDIT_EMPLOYMENT_START_DATE.NAME,
              )
            }
          />
          <LocationDropDown
            disabled={!shouldAllowLocationEdit}
            defaultValue={location?.objectId}
            onLocationChange={(location: string) => updateLocation(location)}
          />
        </div>
        <div className={styles.btnsContainer}>
          {inactive ? (
            <button
              className={styles.btn}
              onClick={() => activeStatusHandler(false)}
            >
              Activate
            </button>
          ) : (
            <button
              className={styles.btn}
              onClick={() => activeStatusHandler(true)}
            >
              Deactivate
            </button>
          )}
        </div>
      </div>

      {/* Deactivation Warning Dialog */}
      <Dialog
        open={showDeactivationWarning}
        onClose={cancelDeactivation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Deactivation Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivating this technician may bump scheduled orders. To safely deactivate:
            <br /><br />
            1. Click "Cancel" below, then click "Manage Schedule"
            <br />
            2. Mark all days as non-working days in the Schedule Pattern (left side)
            <br />
            3. Click "Check Changes" to see which orders would be bumped
            <br />
            4. Handle the order impacts appropriately
            <br />
            5. Return here and deactivate safely
            <br /><br />
            Do you want to proceed with deactivation anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeactivation} color="primary">
            Cancel - Let me check schedule first
          </Button>
          <Button onClick={confirmDeactivation} color="error" variant="contained">
            Proceed with deactivation
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
